import {
  CircleChevronDown,
  CirclePlay,
  Eye,
  FileSearch,
  FlaskConical,
  Globe,
  Loader2,
  Pencil,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../../../components/ui/common/badge";
import { Button } from "../../../../components/ui/common/button";
import { TableCell, TableRow } from "../../../../components/ui/common/table";
import { cn } from "../../../../lib/utils";
import { getRecipeRowActions } from "../recipeList.permissions";
import {
  hasMultipleVariants,
  isDeletedRecipe,
  isDemoRecipe,
  isPublicRecipe,
  toRecipeRecord
} from "../recipeList.utils";
import {
  RecipeGroupRecord,
  RecipeListRowHandlers,
  RecipeRowActionType
} from "../types";
import ImpactChangeDisplay from "./ImpactChangeDisplay";
import CreatedByCell from "./CreatedByCell";
import { recipeListTableColumnClass } from "./recipeListTable.columns";

interface RecipeListTableRowProps {
  recipe: RecipeGroupRecord;
  isAdmin: boolean;
  currentUserId?: string;
  handlers: RecipeListRowHandlers;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const actionButtonClass = "tw-h-8 tw-w-8";

interface ActionButtonProps {
  action: RecipeRowActionType;
  recipeId: string;
  title: string;
  handlers: RecipeListRowHandlers;
  onClick: (event: React.MouseEvent) => void;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const ActionButton = ({
  action,
  recipeId,
  title,
  handlers,
  onClick,
  className,
  disabled = false,
  children
}: ActionButtonProps) => {
  const loading = handlers.isActionLoading(recipeId, action);
  const rowBusy = handlers.isRowBusy(recipeId);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(actionButtonClass, className)}
      disabled={disabled || rowBusy}
      onClick={onClick}
      title={title}
      aria-busy={loading}
    >
      {loading ? (
        <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
      ) : (
        children
      )}
    </Button>
  );
};

const RecipeListTableRow = ({
  recipe,
  isAdmin,
  currentUserId,
  handlers,
  isExpanded,
  onToggleExpand
}: RecipeListTableRowProps) => {
  const recipeRecord = toRecipeRecord(recipe);
  const actions = getRecipeRowActions(recipeRecord, { isAdmin, currentUserId });
  const rowBusy = handlers.isRowBusy(recipe.id);
  const multipleVariants = hasMultipleVariants(recipe);
  const primaryVariant = recipe.variants[0];

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!multipleVariants) {
      toast.info("No additional variants to expand", { style: { background: "#fff" } });
      return;
    }

    onToggleExpand();
  };

  const ExpandButton = () => (
    <button
      type="button"
      onClick={handleExpandClick}
      className="tw-inline-flex tw-items-center tw-justify-center tw-text-primary hover:tw-text-primary/80"
      title={isExpanded ? "Collapse variants" : "Expand variants"}
      aria-expanded={multipleVariants ? isExpanded : undefined}
      aria-label={isExpanded ? "Collapse variants" : "Expand variants"}
    >
      {isExpanded && multipleVariants ? (
        <CircleChevronDown className="tw-h-5 tw-w-5" />
      ) : (
        <CirclePlay className="tw-h-5 tw-w-5" />
      )}
    </button>
  );

  if (isDeletedRecipe(recipeRecord)) {
    return (
      <TableRow className="tw-bg-muted/20 tw-text-muted-foreground">
        <TableCell className={recipeListTableColumnClass.expand}>
          <ExpandButton />
        </TableCell>
        <TableCell className={recipeListTableColumnClass.recipeName}>{recipe.recipeName}</TableCell>
        <TableCell className={recipeListTableColumnClass.variation}>
          {!isAdmin && (
            <Badge variant="destructive" className="tw-text-xs">
              Removed
            </Badge>
          )}
        </TableCell>
        <TableCell className={recipeListTableColumnClass.numeric} />
        <TableCell className={recipeListTableColumnClass.numeric} />
        <TableCell className={recipeListTableColumnClass.impactChange} />
        <TableCell className={recipeListTableColumnClass.recipeType}>
          <Badge variant="destructive" className="tw-text-xs">
            Removed
          </Badge>
        </TableCell>
        <TableCell className={recipeListTableColumnClass.date}>{recipe.updatedDate}</TableCell>
        <TableCell className={recipeListTableColumnClass.date} />
        <TableCell className={cn(recipeListTableColumnClass.createdBy, "tw-text-muted-foreground tw-text-sm")}>
          <CreatedByCell name={recipe.createdByName} />
        </TableCell>
        <TableCell className={recipeListTableColumnClass.actions}>
          {actions.canViewDemoKitUsage && (
            <ActionButton
              action="usage"
              recipeId={recipe.id}
              title="View Demo Kit Usage"
              handlers={handlers}
              onClick={event => handlers.onUsage(event, recipe)}
            >
              <Eye className="tw-h-3.5 tw-w-3.5" />
            </ActionButton>
          )}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        className={cn(
          "tw-bg-white hover:tw-bg-muted/50 tw-cursor-pointer",
          recipe.needsRecalculation && "tw-bg-amber-50/60",
          rowBusy && "tw-pointer-events-none tw-opacity-70"
        )}
        onClick={() => {
          if (!rowBusy) {
            handlers.onRowClick(recipe);
          }
        }}
      >
        <TableCell
          className={recipeListTableColumnClass.expand}
          onClick={event => event.stopPropagation()}
        >
          <ExpandButton />
        </TableCell>
        <TableCell className={cn(recipeListTableColumnClass.recipeName, "tw-font-medium tw-text-foreground")}>
          {recipe.recipeName}
        </TableCell>
        <TableCell className={cn(recipeListTableColumnClass.variation, "tw-text-foreground")}>
          {multipleVariants ? (
            <button
              type="button"
              className="tw-text-primary tw-underline hover:tw-text-primary/80"
              onClick={event => {
                event.stopPropagation();
                onToggleExpand();
              }}
            >
              Multiple
            </button>
          ) : (
            primaryVariant?.variationName ?? "-"
          )}
        </TableCell>
        {!multipleVariants && primaryVariant ? (
          <>
            <TableCell className={cn(recipeListTableColumnClass.numeric, "tw-text-foreground tw-tabular-nums")}>
              {primaryVariant.baselineImpact.toFixed(4)}
            </TableCell>
            <TableCell className={cn(recipeListTableColumnClass.numeric, "tw-text-foreground tw-tabular-nums")}>
              {primaryVariant.variationImpact.toFixed(4)}
            </TableCell>
            <TableCell className={recipeListTableColumnClass.impactChange}>
              <ImpactChangeDisplay change={primaryVariant.impactChange} />
            </TableCell>
          </>
        ) : (
          <>
            <TableCell className={recipeListTableColumnClass.numeric} />
            <TableCell className={recipeListTableColumnClass.numeric} />
            <TableCell className={recipeListTableColumnClass.impactChange} />
          </>
        )}
        <TableCell className={recipeListTableColumnClass.recipeType}>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1">
            <Badge variant={isDemoRecipe(recipeRecord) ? "secondary" : "outline"} className="tw-text-xs">
              {isDemoRecipe(recipeRecord) ? "Demo" : "Custom"}
            </Badge>
            {isPublicRecipe(recipeRecord) && (
              <Badge variant="outline" className="tw-text-xs">
                Public
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className={cn(recipeListTableColumnClass.date, "tw-text-muted-foreground tw-text-sm")}>
          {!multipleVariants ? recipe.updatedDate : ""}
        </TableCell>
        <TableCell className={cn(recipeListTableColumnClass.date, "tw-text-muted-foreground tw-text-sm")}>
          {!multipleVariants ? recipe.calculatedDate : ""}
        </TableCell>
        <TableCell className={cn(recipeListTableColumnClass.createdBy, "tw-text-muted-foreground tw-text-sm")}>
          <CreatedByCell name={recipe.createdByName} />
        </TableCell>
        <TableCell className={recipeListTableColumnClass.actions}>
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-1"
            onClick={event => event.stopPropagation()}
          >
            {actions.canEdit && (
              <ActionButton
                action="edit"
                recipeId={recipe.id}
                title="Edit"
                handlers={handlers}
                onClick={event => handlers.onEdit(event, recipe)}
              >
                <Pencil className="tw-h-3.5 tw-w-3.5" />
              </ActionButton>
            )}
            {!actions.canEdit && actions.canView && (
              <ActionButton
                action="view"
                recipeId={recipe.id}
                title="View"
                handlers={handlers}
                onClick={event => handlers.onView(event, recipe)}
              >
                <FileSearch className="tw-h-3.5 tw-w-3.5" />
              </ActionButton>
            )}
            {actions.canDelete && (
              <ActionButton
                action="delete"
                recipeId={recipe.id}
                title="Delete"
                handlers={handlers}
                className="tw-text-destructive hover:tw-text-destructive"
                onClick={event => handlers.onDelete(event, recipe)}
              >
                <Trash2 className="tw-h-3.5 tw-w-3.5" />
              </ActionButton>
            )}
            {actions.canViewDemoKitUsage && (
              <ActionButton
                action="usage"
                recipeId={recipe.id}
                title="View Demo Kit Usage"
                handlers={handlers}
                onClick={event => handlers.onUsage(event, recipe)}
              >
                <Eye className="tw-h-3.5 tw-w-3.5" />
              </ActionButton>
            )}
            {actions.canToggleDemoKit && (
              <ActionButton
                action="demo"
                recipeId={recipe.id}
                title={isDemoRecipe(recipeRecord) ? "Remove Demo Kit" : "Convert to Demo Kit"}
                handlers={handlers}
                disabled={actions.isDemoKitToggleDisabled}
                onClick={event => handlers.onToggleDemo(event, recipe)}
              >
                <FlaskConical
                  className={cn(
                    "tw-h-3.5 tw-w-3.5",
                    isDemoRecipe(recipeRecord) ? "tw-text-primary" : "tw-text-muted-foreground",
                    actions.isDemoKitToggleDisabled && "tw-opacity-50"
                  )}
                />
              </ActionButton>
            )}
            {(actions.canMakePublic || actions.canMakePrivate) && (
              <ActionButton
                action={actions.canMakePrivate ? "private" : "public"}
                recipeId={recipe.id}
                title={actions.canMakePrivate ? "Make Private" : "Make Public"}
                handlers={handlers}
                onClick={event => handlers.onTogglePublic(event, recipe)}
              >
                <Globe
                  className={cn(
                    "tw-h-3.5 tw-w-3.5",
                    actions.canMakePrivate ? "tw-text-primary" : "tw-text-muted-foreground"
                  )}
                />
              </ActionButton>
            )}
          </div>
        </TableCell>
      </TableRow>

      {isExpanded &&
        multipleVariants &&
        recipe.variants.map(variant => (
          <TableRow
            key={`${recipe.id}-${variant.variantId}`}
            className={cn(
              "tw-bg-muted/20 hover:tw-bg-muted/30",
              recipe.needsRecalculation && "tw-bg-amber-50/60"
            )}
          >
            <TableCell className={recipeListTableColumnClass.expand} />
            <TableCell className={recipeListTableColumnClass.recipeName} />
            <TableCell className={cn(recipeListTableColumnClass.variation, "tw-text-foreground")}>
              {variant.variationName}
            </TableCell>
            <TableCell className={cn(recipeListTableColumnClass.numeric, "tw-text-foreground tw-tabular-nums")}>
              {variant.baselineImpact.toFixed(4)}
            </TableCell>
            <TableCell className={cn(recipeListTableColumnClass.numeric, "tw-text-foreground tw-tabular-nums")}>
              {variant.variationImpact.toFixed(4)}
            </TableCell>
            <TableCell className={recipeListTableColumnClass.impactChange}>
              <ImpactChangeDisplay change={variant.impactChange} />
            </TableCell>
            <TableCell className={recipeListTableColumnClass.recipeType} />
            <TableCell className={cn(recipeListTableColumnClass.date, "tw-text-muted-foreground tw-text-sm")}>
              {recipe.updatedDate}
            </TableCell>
            <TableCell className={cn(recipeListTableColumnClass.date, "tw-text-muted-foreground tw-text-sm")}>
              {recipe.calculatedDate}
            </TableCell>
            <TableCell className={recipeListTableColumnClass.createdBy} />
            <TableCell className={recipeListTableColumnClass.actions} />
          </TableRow>
        ))}
    </>
  );
};

export default RecipeListTableRow;
